import React, { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import NamedBox from '../atoms/NamedBox';
import Dialog from '../molecules/Dialog';
import PieChartComponent from '../atoms/PieChartComponent';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import {
  COLOR_BORDER,
  COLOR_BLUE,
  COLOR_DARK_BLUE,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_PURPLE,
  COLOR_TEXT,
  COLOR_WHITE,
  COLOR_BACKGROUND_WARM,
  COLOR_LIGHT_GRAY,
  COLOR_DARK_GRAY,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import MEDIA from '../styles/media';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import ListItem from '../atoms/ListItem';
import SchoolIcon from '../icons/SchoolIcon';
import ClassIcon from '../icons/ClassIcon';
import TransferPortalIcon from '../icons/TransferPortalIcon';
import { CollegeProductionRow } from '../types/CollegeProductionData';
import PFFGameGradeLogTable, { PFFGameLogRow } from '../molecules/PFFGameGradeLogTable';
import gql from '../services/gql';
import { useQuery } from '@tanstack/react-query';
import Player from '../types/Player';
import HighSchool from '../types/HighSchool';
import College from '../types/College';

interface ColDef {
  key: keyof CollegeProductionRow | string;
  label: string;
  format?: 'number' | 'percent' | 'decimal';
  highlightRel?: boolean;
}

type TabKey = 'passing' | 'rushing' | 'receiving' | 'defense';

const COMMON_COLS: ColDef[] = [
  { key: 'year', label: 'Year' },
  { key: 'teamName', label: 'Team' },
  { key: 'gamesPlayed', label: 'G' },
  { key: 'gamesStarted', label: 'GS' },
];

const PASSING_COLS: ColDef[] = [
  { key: 'passingComp', label: 'Comp' },
  { key: 'passingAtt', label: 'Att' },
  { key: 'passingCompletionPercent', label: '%', format: 'percent' },
  { key: 'passingYds', label: 'Yds' },
  { key: 'passingRelPassYds', label: 'Rel Pass Yds', format: 'percent', highlightRel: true },
  { key: 'passingTD', label: 'TDs' },
  { key: 'passingInt', label: 'Ints' },
  { key: 'passingYPA', label: 'YPA', format: 'decimal' },
  { key: 'passingYPG', label: 'YPG', format: 'decimal' },
  { key: 'passingRZComp', label: 'RZ Comp' },
  { key: 'passingRZAtt', label: 'RZ Att' },
  { key: 'passingRZTD', label: 'RZ TDs' },
  { key: 'passingRZInt', label: 'RZ Ints' },
  { key: 'passingIntPercent', label: 'Int %', format: 'percent' },
  { key: 'passingEffPercent', label: 'Eff %', format: 'percent' },
  { key: 'passingFD', label: 'FDs' },
];

const RUSHING_COLS: ColDef[] = [
  { key: 'rushingAtt', label: 'Att' },
  { key: 'rushingYds', label: 'Yds' },
  { key: 'rushingYPC', label: 'YPC', format: 'decimal' },
  { key: 'rushingYPG', label: 'YPG', format: 'decimal' },
  { key: 'rushingRelRushYds', label: 'Rel Rush Yds', format: 'percent', highlightRel: true },
  { key: 'rushingRelYPC', label: 'Rel YPC', format: 'percent', highlightRel: true },
  { key: 'rushingTD', label: 'TD' },
  { key: 'rushingEffRuns', label: 'Eff Runs' },
  { key: 'rushing5PlusRuns', label: '5+' },
  { key: 'rushing10PlusRuns', label: '10+' },
  { key: 'rushing15PlusRuns', label: '15+' },
  { key: 'rushing20PlusRuns', label: '20+' },
  { key: 'rushingFD', label: 'FD' },
];

const RECEIVING_COLS: ColDef[] = [
  { key: 'receiving', label: 'Rec' },
  { key: 'receivingTar', label: 'Tar' },
  { key: 'receivingYds', label: 'Yds' },
  { key: 'receivingYPC', label: 'YPC', format: 'decimal' },
  { key: 'receivingTD', label: 'TD' },
  { key: 'receivingYPG', label: 'YPG', format: 'decimal' },
  { key: 'receivingRZRec', label: 'RZ Rec' },
  { key: 'receivingRZTar', label: 'RZ Tar' },
  { key: 'receiving3rdDnRec', label: '3rd Dn Rec' },
  { key: 'receiving3rdDnTar', label: '3rd Dn Tar' },
  { key: 'receiving10PlusPercent', label: '10+ %', format: 'percent' },
  { key: 'receiving15PlusPercent', label: '15+ %', format: 'percent' },
  { key: 'receiving25PlusPercent', label: '25+ %', format: 'percent' },
  { key: 'receiving40PlusPercent', label: '40+ %', format: 'percent' },
  { key: 'receivingFD', label: 'FD' },
  { key: 'receivingDrop', label: 'Drop' },
];

const DEFENSE_COLS: ColDef[] = [
  { key: 'defenseSoloTackles', label: 'Solo' },
  { key: 'defenseAssistedTackles', label: 'Ast' },
  { key: 'defenseTFL', label: 'TFL', format: 'decimal' },
  { key: 'defenseTFLYds', label: 'TFL Yds' },
  { key: 'defenseSack', label: 'Sack', format: 'decimal' },
  { key: 'defenseSackYds', label: 'Sack Yds' },
  { key: 'defenseQBHurry', label: 'QBH' },
  { key: 'defensePassBreak', label: 'PB' },
  { key: 'defenseInt', label: 'Int' },
  { key: 'defenseIntRetYds', label: 'Int Ret Yds' },
  { key: 'defenseIntRetTD', label: 'Int Ret TD' },
  { key: 'defenseFR', label: 'FR' },
  { key: 'defenseFRYds', label: 'FR Yds' },
  { key: 'defenseFRTD', label: 'FR TD' },
  { key: 'defenseFF', label: 'FF' },
  { key: 'defenseSafety', label: 'Safety' },
  { key: 'defenseBlockKick', label: 'Block' },
];

const TAB_COLS: Record<TabKey, ColDef[]> = {
  passing: PASSING_COLS,
  rushing: RUSHING_COLS,
  receiving: RECEIVING_COLS,
  defense: DEFENSE_COLS,
};

// const MOCK_PRODUCTION_RESPONSE: ProductionDataResponse = {
//   idType: 'tfPlayerId',
//   idValue: 10000,
//   productionData: [
//     {
//       year: 2025,
//       teamIdType: 'tfTeamId',
//       teamIdValue: 124,
//       teamName: 'Auburn',
//       gamesPlayed: 9,
//       gamesStarted: 9,
//       passingComp: 202,
//       passingAtt: 299,
//       passingCompletionPercent: 67.6,
//       passingYds: 0,
//       passingRelPassYds: 112.5,
//       passingTD: 14,
//       passingInt: 5,
//       passingYPA: 7.8,
//       passingYPG: 258.1,
//       passingRZComp: 20,
//       passingRZAtt: 31,
//       passingRZTD: 4,
//       passingRZInt: 1,
//       passingIntPercent: 1.7,
//       passingEffPercent: 49.8,
//       passingFD: 106,
//       rushingAtt: 74,
//       rushingYds: 107,
//       rushingYPC: 1.4,
//       rushingYPG: 9.7,
//       rushingRelRushYds: 5.7,
//       rushingRelYPC: 6.6,
//       rushingTD: 4,
//       rushingEffRuns: 33,
//       rushing5PlusRuns: 19,
//       rushing10PlusRuns: 0,
//       rushing15PlusRuns: 0,
//       rushing20PlusRuns: 0,
//       rushingFD: 15,
//       receiving: 0,
//       receivingTar: 0,
//       receivingYds: 0,
//       receivingYPC: 0,
//       receivingTD: 0,
//       receivingYPG: 0,
//       receivingRZRec: 0,
//       receivingRZTar: 0,
//       receiving3rdDnRec: 0,
//       receiving3rdDnTar: 0,
//       receiving10PlusPercent: 0,
//       receiving15PlusPercent: 0,
//       receiving25PlusPercent: 0,
//       receiving40PlusPercent: 0,
//       receivingFD: 0,
//       receivingDrop: 0,
//       defenseSoloTackles: 71,
//       defenseAssistedTackles: 70,
//       defenseTFL: 7.5,
//       defenseTFLYds: 17,
//       defenseSack: 1.5,
//       defenseSackYds: 10,
//       defenseQBHurry: 2,
//       defensePassBreak: 1,
//       defenseInt: 1,
//       defenseIntRetYds: 0,
//       defenseIntRetTD: 0,
//       defenseFR: 0,
//       defenseFRYds: 0,
//       defenseFRTD: 0,
//       defenseFF: 1,
//       defenseSafety: 0,
//       defenseBlockKick: 0,
//     },
//     {
//       year: 2024,
//       teamIdType: 'tfTeamId',
//       teamIdValue: 98,
//       teamName: 'Purdue',
//       gamesPlayed: 11,
//       gamesStarted: 10,
//       passingComp: 245,
//       passingAtt: 380,
//       passingCompletionPercent: 64.5,
//       passingYds: 0,
//       passingRelPassYds: 98.2,
//       passingTD: 18,
//       passingInt: 7,
//       passingYPA: 7.2,
//       passingYPG: 242.3,
//       passingRZComp: 18,
//       passingRZAtt: 28,
//       passingRZTD: 3,
//       passingRZInt: 2,
//       passingIntPercent: 1.8,
//       passingEffPercent: 48.2,
//       passingFD: 118,
//       rushingAtt: 52,
//       rushingYds: 89,
//       rushingYPC: 1.7,
//       rushingYPG: 8.1,
//       rushingRelRushYds: 4.2,
//       rushingRelYPC: 5.1,
//       rushingTD: 2,
//       rushingEffRuns: 28,
//       rushing5PlusRuns: 12,
//       rushing10PlusRuns: 2,
//       rushing15PlusRuns: 0,
//       rushing20PlusRuns: 0,
//       rushingFD: 8,
//       receiving: 0,
//       receivingTar: 0,
//       receivingYds: 0,
//       receivingYPC: 0,
//       receivingTD: 0,
//       receivingYPG: 0,
//       receivingRZRec: 0,
//       receivingRZTar: 0,
//       receiving3rdDnRec: 0,
//       receiving3rdDnTar: 0,
//       receiving10PlusPercent: 0,
//       receiving15PlusPercent: 0,
//       receiving25PlusPercent: 0,
//       receiving40PlusPercent: 0,
//       receivingFD: 0,
//       receivingDrop: 0,
//       defenseSoloTackles: 45,
//       defenseAssistedTackles: 38,
//       defenseTFL: 5.0,
//       defenseTFLYds: 12,
//       defenseSack: 1.0,
//       defenseSackYds: 6,
//       defenseQBHurry: 1,
//       defensePassBreak: 2,
//       defenseInt: 0,
//       defenseIntRetYds: 0,
//       defenseIntRetTD: 0,
//       defenseFR: 0,
//       defenseFRYds: 0,
//       defenseFRTD: 0,
//       defenseFF: 0,
//       defenseSafety: 0,
//       defenseBlockKick: 0,
//     },
//     {
//       year: 2023,
//       teamIdType: 'tfTeamId',
//       teamIdValue: 87,
//       teamName: 'Notre Dame',
//       gamesPlayed: 12,
//       gamesStarted: 11,
//       passingComp: 198,
//       passingAtt: 310,
//       passingCompletionPercent: 63.9,
//       passingYds: 0,
//       passingRelPassYds: 95.0,
//       passingTD: 12,
//       passingInt: 6,
//       passingYPA: 6.9,
//       passingYPG: 198.5,
//       passingRZComp: 15,
//       passingRZAtt: 25,
//       passingRZTD: 2,
//       passingRZInt: 1,
//       passingIntPercent: 1.9,
//       passingEffPercent: 46.5,
//       passingFD: 92,
//       rushingAtt: 41,
//       rushingYds: 72,
//       rushingYPC: 1.8,
//       rushingYPG: 6.0,
//       rushingRelRushYds: 3.8,
//       rushingRelYPC: 4.9,
//       rushingTD: 1,
//       rushingEffRuns: 22,
//       rushing5PlusRuns: 8,
//       rushing10PlusRuns: 1,
//       rushing15PlusRuns: 0,
//       rushing20PlusRuns: 0,
//       rushingFD: 6,
//       receiving: 0,
//       receivingTar: 0,
//       receivingYds: 0,
//       receivingYPC: 0,
//       receivingTD: 0,
//       receivingYPG: 0,
//       receivingRZRec: 0,
//       receivingRZTar: 0,
//       receiving3rdDnRec: 0,
//       receiving3rdDnTar: 0,
//       receiving10PlusPercent: 0,
//       receiving15PlusPercent: 0,
//       receiving25PlusPercent: 0,
//       receiving40PlusPercent: 0,
//       receivingFD: 0,
//       receivingDrop: 0,
//       defenseSoloTackles: 38,
//       defenseAssistedTackles: 32,
//       defenseTFL: 4.0,
//       defenseTFLYds: 9,
//       defenseSack: 0.5,
//       defenseSackYds: 4,
//       defenseQBHurry: 0,
//       defensePassBreak: 1,
//       defenseInt: 0,
//       defenseIntRetYds: 0,
//       defenseIntRetTD: 0,
//       defenseFR: 0,
//       defenseFRYds: 0,
//       defenseFRTD: 0,
//       defenseFF: 0,
//       defenseSafety: 0,
//       defenseBlockKick: 0,
//     },
//     {
//       year: 2022,
//       teamIdType: 'tfTeamId',
//       teamIdValue: 87,
//       teamName: 'Notre Dame',
//       gamesPlayed: 10,
//       gamesStarted: 8,
//       passingComp: 156,
//       passingAtt: 265,
//       passingCompletionPercent: 58.9,
//       passingYds: 0,
//       passingRelPassYds: 32.5,
//       passingTD: 9,
//       passingInt: 8,
//       passingYPA: 6.2,
//       passingYPG: 172.4,
//       passingRZComp: 12,
//       passingRZAtt: 22,
//       passingRZTD: 1,
//       passingRZInt: 2,
//       passingIntPercent: 3.0,
//       passingEffPercent: 42.1,
//       passingFD: 74,
//       rushingAtt: 28,
//       rushingYds: 45,
//       rushingYPC: 1.6,
//       rushingYPG: 4.5,
//       rushingRelRushYds: 2.1,
//       rushingRelYPC: 3.2,
//       rushingTD: 0,
//       rushingEffRuns: 15,
//       rushing5PlusRuns: 4,
//       rushing10PlusRuns: 0,
//       rushing15PlusRuns: 0,
//       rushing20PlusRuns: 0,
//       rushingFD: 3,
//       receiving: 0,
//       receivingTar: 0,
//       receivingYds: 0,
//       receivingYPC: 0,
//       receivingTD: 0,
//       receivingYPG: 0,
//       receivingRZRec: 0,
//       receivingRZTar: 0,
//       receiving3rdDnRec: 0,
//       receiving3rdDnTar: 0,
//       receiving10PlusPercent: 0,
//       receiving15PlusPercent: 0,
//       receiving25PlusPercent: 0,
//       receiving40PlusPercent: 0,
//       receivingFD: 0,
//       receivingDrop: 0,
//       defenseSoloTackles: 22,
//       defenseAssistedTackles: 18,
//       defenseTFL: 2.5,
//       defenseTFLYds: 5,
//       defenseSack: 0,
//       defenseSackYds: 0,
//       defenseQBHurry: 0,
//       defensePassBreak: 0,
//       defenseInt: 0,
//       defenseIntRetYds: 0,
//       defenseIntRetTD: 0,
//       defenseFR: 0,
//       defenseFRYds: 0,
//       defenseFRTD: 0,
//       defenseFF: 0,
//       defenseSafety: 0,
//       defenseBlockKick: 0,
//     },
//   ],
// };

function formatCellValue(value: number, col: ColDef): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (col.format === 'percent') return `${value}%`;
  if (col.format === 'decimal') return String(value);
  return String(Math.round(value));
}

function getCellValue(row: CollegeProductionRow | TotalsRow, key: string): number | string {
  const r = row as unknown as Record<string, number | string>;
  return r[key] != null ? r[key] : '—';
}

interface TotalsRow extends Partial<CollegeProductionRow> {
  _isTotals: true;
  year: number;
  teamName: string;
}

function computeTotals(rows: CollegeProductionRow[], cols: ColDef[]): TotalsRow {
  const totals: TotalsRow = { _isTotals: true, year: 0, teamName: 'Totals' };
  const sumKeys = new Set(['gamesPlayed', 'gamesStarted', 'passingComp', 'passingAtt', 'passingYds', 'passingTD', 'passingInt', 'passingRZComp', 'passingRZAtt', 'passingRZTD', 'passingRZInt', 'passingFD', 'rushingAtt', 'rushingYds', 'rushingTD', 'rushingEffRuns', 'rushing5PlusRuns', 'rushing10PlusRuns', 'rushing15PlusRuns', 'rushing20PlusRuns', 'rushingFD', 'receiving', 'receivingTar', 'receivingYds', 'receivingTD', 'receivingRZRec', 'receivingRZTar', 'receiving3rdDnRec', 'receiving3rdDnTar', 'receivingFD', 'receivingDrop', 'defenseSoloTackles', 'defenseAssistedTackles', 'defenseTFLYds', 'defenseSackYds', 'defenseQBHurry', 'defensePassBreak', 'defenseInt', 'defenseIntRetYds', 'defenseIntRetTD', 'defenseFR', 'defenseFRYds', 'defenseFRTD', 'defenseFF', 'defenseSafety', 'defenseBlockKick']);
  const tot = totals as unknown as Record<string, number>;
  const rowRec = (r: CollegeProductionRow) => r as unknown as Record<string, number>;
  for (const col of cols) {
    const k = col.key as string;
    if (sumKeys.has(k)) {
      tot[k] = rows.reduce((s, row) => s + (Number(rowRec(row)[k]) || 0), 0);
    }
  }
  const avgKeys: Record<string, { num: string; den: string }> = {
    passingCompletionPercent: { num: 'passingComp', den: 'passingAtt' },
    passingYPA: { num: 'passingYds', den: 'passingAtt' },
    passingYPG: { num: 'passingYds', den: 'gamesPlayed' },
    passingIntPercent: { num: 'passingInt', den: 'passingAtt' },
    rushingYPC: { num: 'rushingYds', den: 'rushingAtt' },
    rushingYPG: { num: 'rushingYds', den: 'gamesPlayed' },
    receivingYPC: { num: 'receivingYds', den: 'receiving' },
    receivingYPG: { num: 'receivingYds', den: 'gamesPlayed' },
    defenseTFL: { num: 'defenseTFL', den: 'gamesPlayed' },
    defenseSack: { num: 'defenseSack', den: 'gamesPlayed' },
  };
  for (const [outKey, { num, den }] of Object.entries(avgKeys)) {
    const n = rows.reduce((s, r) => s + (Number(rowRec(r)[num]) || 0), 0);
    const d = rows.reduce((s, r) => s + (Number(rowRec(r)[den]) || 0), 0);
    tot[outKey] = d ? Math.round((n / d) * 100) / 100 : 0;
  }
  const relKeys = ['passingRelPassYds', 'passingEffPercent', 'rushingRelRushYds', 'rushingRelYPC', 'receiving10PlusPercent', 'receiving15PlusPercent', 'receiving25PlusPercent', 'receiving40PlusPercent'];
  for (const k of relKeys) {
    const vals = rows.map((r) => rowRec(r)[k]).filter((v) => typeof v === 'number');
    tot[k] = vals.length ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : 0;
  }
  return totals;
}

export interface ProductionTableAction {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface CollegeNFLProductionTableProps {
  className?: string;
  player?: Player;
  data?: CollegeProductionRow[] | null;
  /** Action links on the right of the first header */
  actions?: ProductionTableAction[];
}

interface PlayerPFFGameLog {
  gameId?: number;
  opponentTeamId?: number;
  hasStarted?: boolean;
  offensiveGradesOverall?: number;
  offensiveGradesPass?: number;
  offensiveGradesRun?: number;
  offensiveGradesRec?: number;
  offensiveGradesRunBlock?: number;
  offensiveGradesPassBlock?: number;
  defensiveGradesOverall?: number;
  defensiveGradesPassRush?: number;
  defensiveGradesRunDef?: number;
  defensiveGradesCover?: number;
  snapsTotal?: number;
  snapsRun?: number;
  snapsPass?: number;
  snapsRec?: number;
  snapsRunBlock?: number;
  snapsPassBlock?: number;
  snapsPassRush?: number;
  snapsRunDef?: number;
  snapsCover?: number;
  createdAt?: string;
  opponentTeam?: {
    name?: string;
  };
  teamScore?: number;
  opponentTeamScore?: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerName: {
    fontFamily: FONT_PROXIMA_NOVA,
  },
  headerInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    flex: 1,
    [MEDIA.MOBILE]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  labelAndTabs: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tab: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: COLOR_DARK_GRAY,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontFamily: FONT_PROXIMA_NOVA,
    '&:hover': {
      color: COLOR_TEXT,
    },
  },
  tabActive: {
    color: COLOR_TEXT,
    borderBottomColor: COLOR_ORANGE,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  actionLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(13),
    color: COLOR_BLUE,
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily: FONT_PROXIMA_NOVA,
    '&:hover': {
      color: COLOR_DARK_BLUE,
      textDecoration: 'underline',
    },
  },
  actionLinkDisabled: {
    color: COLOR_DARK_GRAY,
    cursor: 'default',
    '&:hover': { textDecoration: 'none' },
  },
  actionIcon: {
    fontSize: 18,
    color: 'inherit',
  },
  tableOuter: {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    [MEDIA.MOBILE]: {
      marginLeft: -theme.spacing(1),
      marginRight: -theme.spacing(1),
      width: `calc(100% + ${theme.spacing(2)}px)`,
    },
  },
  tableWrapper: {
    maxHeight: 400,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  table: {
    width: '100%',
    minWidth: 800,
    borderCollapse: 'collapse' as const,
    fontSize: theme.typography.pxToRem(13),
  },
  thead: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    backgroundColor: COLOR_WHITE,
  },
  th: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    borderRight: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(0.75, 1),
    fontWeight: 700,
    color: COLOR_TEXT,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    backgroundColor: COLOR_BACKGROUND_WARM,
  },
  thFirst: {
    textAlign: 'left' as const,
    minWidth: 56,
  },
  thSecond: {
    textAlign: 'left' as const,
    minWidth: 90,
  },
  bodyRow: {
    backgroundColor: COLOR_WHITE,
  },
  bodyRowAlt: {
    backgroundColor: COLOR_LIGHT_GRAY,
  },
  totalsRow: {
    backgroundColor: COLOR_MIDDLE_GRAY,
    fontWeight: 600,
  },
  td: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
    borderRight: `1px solid ${COLOR_BORDER}`,
    padding: theme.spacing(0.75, 1),
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
  },
  tdFirst: {
    textAlign: 'left' as const,
    fontWeight: 600,
  },
  tdSecond: {
    textAlign: 'left' as const,
  },
  cellHighlight: {
    color: COLOR_PURPLE,
  },
  usageRoot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [MEDIA.MOBILE]: {
      flexDirection: 'column',
      padding: theme.spacing(2),
      gap: theme.spacing(3),
    },
  },
  usageChartWrapper: {
    flex: '0 0 360px',
    maxWidth: 420,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  usageInnerLabel: {
    position: 'absolute',
    textAlign: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(18),
    color: COLOR_TEXT,
    pointerEvents: 'none',
  },
  usageSideCard: {
    flex: '0 0 320px',
    maxWidth: 360,
    backgroundColor: COLOR_WHITE,
    borderRadius: 16,
    border: `1px solid ${COLOR_BORDER}`,
    boxShadow: `0 10px 20px 0 rgba(0,0,0,0.06)`,
    padding: theme.spacing(3),
    fontFamily: FONT_PROXIMA_NOVA,
  },
  usageSideTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: COLOR_DARK_GRAY,
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  usageTotalPlays: {
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(2.5),
  },
  usageSection: {
    borderTop: `1px solid ${COLOR_BORDER}`,
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  usageSectionHeader: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  usageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: theme.typography.pxToRem(12),
    marginBottom: theme.spacing(0.5),
  },
  usageRowLabel: {
    color: COLOR_DARK_GRAY,
  },
  usageRowValue: {
    fontWeight: 600,
  },
  divider: {
    width: '1px',
    height: '30px',
    margin: theme.spacing(0, 1),
    background: COLOR_MIDDLE_GRAY,
  },
  gameLogLoaderWrapper: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gameLogEmptyState: {
    minHeight: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(14),
  },
  fullGameLogDialogContent: {
    width: '96%',
    maxWidth: 1240,
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 2),
  },
  dividedContentSection: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '30px',
      paddingRight: theme.spacing(2),
      borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,
  
      '&:last-child': {
        paddingRight: 0,
        borderRight: 0,
      },
    },
}));

const DEFAULT_ACTIONS: ProductionTableAction[] = [
  { label: 'QB Profiler', icon: <AccountTreeIcon /> },
  { label: 'Rankings', icon: <TrendingUpIcon /> },
  { label: 'Usage', icon: <DonutLargeIcon /> },
  { label: 'Career', icon: <EmojiEventsIcon /> },
  { label: 'Grades', icon: <span style={{ fontWeight: 700, fontSize: 11 }}>PFF</span> },
];

export default function CollegeNFLProductionTable(props: CollegeNFLProductionTableProps) {
  const { className, player, data, actions } = props;
  const actionList = actions ?? DEFAULT_ACTIONS;
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState<TabKey>('passing');
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
  const [isCareerDialogOpen, setIsCareerDialogOpen] = useState(false);
  const [isGradesDialogOpen, setIsGradesDialogOpen] = useState(false);

  const { data: playerGameLogData, isLoading: isPlayerGameLogLoading } = useQuery<PlayerPFFGameLog[]>(
    ['playerPFFGameLog', player?.id],
    () => gql(`
      getPlayerGameLog(playerId: ${player?.id || 0}) {
        gameId
        teamId
        opponentTeamId
        hasStarted
        offensiveGradesOverall
        offensiveGradesPass
        offensiveGradesRun
        offensiveGradesRec
        offensiveGradesRunBlock
        offensiveGradesPassBlock
        defensiveGradesOverall
        defensiveGradesPassRush
        defensiveGradesRunDef
        defensiveGradesCover
        snapsTotal
        snapsRun
        snapsPass
        snapsRec
        snapsRunBlock
        snapsPassBlock
        snapsPassRush
        snapsRunDef
        snapsCover
        createdAt
        opponentTeam {
          name
        }
        teamScore
        opponentTeamScore
      }
    `).then((queryData: any) => (queryData?.getPlayerGameLog || []) as PlayerPFFGameLog[]),
    {
      enabled: !!player?.id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    },
  );

  const primarySchool = ((player && player.highSchools) || [])
      .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);
  
  const primaryCollege = (player?.playerColleges || [])
      .find((college:College) => !!college?.isPrimary);

  const fullGameLogRows: PFFGameLogRow[] = useMemo(() => {
    if (!playerGameLogData || playerGameLogData.length === 0) return [];

    return playerGameLogData.map((row, idx) => {
      const createdDate = row.createdAt ? new Date(row.createdAt) : null;
      const formattedDate = createdDate && !Number.isNaN(createdDate.getTime())
        ? `${String(createdDate.getMonth() + 1).padStart(2, '0')}/${String(createdDate.getDate()).padStart(2, '0')}/${String(createdDate.getFullYear()).slice(-2)}`
        : '--/--/--';

      return {
        id: String(row.gameId || idx),
        date: formattedDate,
        score: (row.teamScore != null && row.opponentTeamScore != null) ? `${row.teamScore}-${row.opponentTeamScore} ${row.teamScore == row.opponentTeamScore ? 'D' : (row.teamScore > row.opponentTeamScore) ? 'W': "L" }` : '',
        opponent: row.opponentTeam?.name || 'Unknown',
        opponentHref: row?.opponentTeam?.name
          ? `/team/${row.opponentTeam.name.toLowerCase().replace(/\s+/g, '-')}`
          : undefined,
        offensive: {
          overall: Number(row.offensiveGradesOverall) || 0,
          pass: Number(row.offensiveGradesPass) || 0,
          run: Number(row.offensiveGradesRun) || 0,
          rec: Number(row.offensiveGradesRec) || 0,
          runBlock: Number(row.offensiveGradesRunBlock) || 0,
          passBlock: Number(row.offensiveGradesPassBlock) || 0,
        },
        defensive: {
          overall: Number(row.defensiveGradesOverall) || 0,
          passRush: Number(row.defensiveGradesPassRush) || 0,
          runDef: Number(row.defensiveGradesRunDef) || 0,
          cover: Number(row.defensiveGradesCover) || 0,
        },
        snaps: {
          total: Number(row.snapsTotal) || 0,
          run: Number(row.snapsRun) || 0,
          pass: Number(row.snapsPass) || 0,
          rec: Number(row.snapsRec) || 0,
          runBlock: Number(row.snapsRunBlock) || 0,
          passBlock: Number(row.snapsPassBlock) || 0,
          passRush: Number(row.snapsPassRush) || 0,
          passDef: Number(row.snapsRunDef) || 0,
          cover: Number(row.snapsCover) || 0,
        },
      };
    });
  }, [playerGameLogData]);

  // const rows = data?.productionData ?? [];
  const rows = data || [];
  const columns = useMemo(() => [...COMMON_COLS, ...TAB_COLS[activeTab]], [activeTab]);
  const totalsRow = useMemo(() => (rows.length ? computeTotals(rows, columns) : null), [rows, columns]);
  const displayRows = useMemo(() => (totalsRow ? [...rows, totalsRow] : rows), [rows, totalsRow]);

  const handleSort = useCallback((key: string) => {
    if (orderBy === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder('asc');
    }
  }, [orderBy]);

  const sortedRows = useMemo(() => {
    if (!orderBy || !rows.length) return displayRows;
    const isTotals = (r: CollegeProductionRow | TotalsRow) => (r as TotalsRow)._isTotals;
    const dataRows = displayRows.filter((r) => !isTotals(r)) as CollegeProductionRow[];
    const tot = displayRows.find(isTotals);
    const sorted = [...dataRows].sort((a, b) => {
      const aVal = getCellValue(a, orderBy);
      const bVal = getCellValue(b, orderBy);
      const an = Number(aVal);
      const bn = Number(bVal);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return order === 'asc' ? an - bn : bn - an;
      return String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
    });
    return tot ? [...sorted, tot] : sorted;
  }, [displayRows, orderBy, order, rows.length]);

  const renderCell = (row: CollegeProductionRow | TotalsRow, col: ColDef) => {
    const isTotals = (row as TotalsRow)._isTotals;
    if (isTotals && col.key === 'year') {
      return (
        <td key={String(col.key)} className={clsx(classes.td, classes.tdFirst)} />
      );
    }
    const raw = getCellValue(row, col.key as string);
    const num = typeof raw === 'number' ? raw : Number(raw);
    const display = col.format != null ? formatCellValue(num, col) : (raw === '—' ? '—' : String(raw));
    const highlight = col.highlightRel && typeof raw === 'number' && !isTotals;
    return (
      <td
        key={String(col.key)}
        className={clsx(
          classes.td,
          col.key === 'year' && classes.tdFirst,
          col.key === 'teamName' && classes.tdSecond,
          highlight && classes.cellHighlight,
        )}
      >
        {display}
      </td>
    );
  };

  function handleActionClick(
    event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement>,
    action: ProductionTableAction,
  ) {
    console.log('action', action)
    if (action.disabled) {
      event.preventDefault();
      return;
    }

    // Allow explicit onClick from props to override default behaviour
    if (action.onClick) {
      event.preventDefault();
      action.onClick();
      return;
    }

    if (action.label === 'Usage') {
      event.preventDefault();
      setIsUsageDialogOpen(true);
      return;
    }

    if (action.label === 'Career') {
      event.preventDefault();
      setIsCareerDialogOpen(true);
      return;
    }

    if (action.label === 'Grades') {
      event.preventDefault();
      setIsGradesDialogOpen(true);
    }
  }

  const usagePieData = [
    { name: 'Left Tackle', value: 399 },
    { name: 'Right Guard', value: 98 },
    { name: 'Right Tackle', value: 2 },
  ];

  const usageColors = ['#0f89e6', '#f47321', '#5eba6d'];

  const usageContent = () => {
    const total = usagePieData.reduce((sum, item) => sum + item.value, 0) || 1;

    return (
      <div className={classes.usageRoot}>
        <div className={classes.usageChartWrapper}>
          <PieChartComponent
            data={usagePieData}
            width={320}
            height={320}
            colors={usageColors}
          />
          <div className={classes.usageInnerLabel}>
            <div>Player</div>
            <div>Positioning</div>
          </div>
        </div>

        {/* middle legend section */}
        <div>
          {usagePieData.map((item, index) => {
            const percent = (item.value / total) * 100;
            return (
              <div key={item.name} className={classes.usageRow}>
                <span className={classes.usageRowLabel}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: usageColors[index % usageColors.length],
                      marginRight: 8,
                    }}
                  />
                  {item.name}
                </span>
                <span className={classes.usageRowValue}>
                  <span>
                  {percent.toFixed(1)}% 
                  </span>
                  <span>
                  {item.value}
                  </span>
                </span>
              </div>
            );
          })}
        </div>

        <div className={classes.usageSideCard}>
          <div className={classes.usageSideTitle}>Total Offensive Plays</div>
          <div className={classes.usageTotalPlays}>498</div>

          <div className={classes.usageSection}>
            <div className={classes.usageSectionHeader}>Receiver</div>
            <div className={classes.usageRow}>
              <span className={classes.usageRowLabel}>50.0% Slot</span>
              <span className={classes.usageRowValue}>50.0% Outside</span>
            </div>
            <div className={classes.usageRow}>
              <span className={classes.usageRowLabel}>19.7% Left</span>
              <span className={classes.usageRowValue}>80.3% Right</span>
            </div>
          </div>

          <div className={classes.usageSection}>
            <div className={classes.usageSectionHeader}>Offensive Line</div>
            <div className={classes.usageRow}>
              <span className={classes.usageRowLabel}>19.7% LT</span>
              <span className={classes.usageRowValue}>0.0% LG</span>
            </div>
            <div className={classes.usageRow}>
              <span className={classes.usageRowLabel}>0.0% C</span>
              <span className={classes.usageRowValue}>0.0% RG</span>
            </div>
            <div className={classes.usageRow}>
              <span className={classes.usageRowLabel}>80.3% RT</span>
              <span className={classes.usageRowValue} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const careerContent = () => {
    const careerTimeline = [
      {
        level: 'High School',
        label: '2007 Grad - Franklin High School (QB)',
        linkLabel: 'Franklin High School',
      },
      {
        level: 'College (2007)',
        label: 'Florida FR (QB)',
        logoText: 'FLA',
      },
      {
        level: 'College (2008)',
        label: 'Florida SO (QB)',
        logoText: 'FLA',
      },
      {
        level: 'College (2010)',
        label: 'Auburn JR (QB)',
        logoText: 'AUB',
      },
      {
        level: 'Transfer Portal',
        isPortal: true,
        fields: [
          { label: 'Entered', value: '01/23/2026' },
          { label: 'Status', value: 'Committed' },
          { label: 'Grad', value: 'Yes' },
        ],
        footerFields: [
          { label: 'NCAA ID', value: '2208641292' },
          { label: 'Division', value: 'II' },
          { label: 'Year', value: '25-27' },
          { label: 'Aid', value: '$' },
          { label: 'DSA', value: 'N/A' },
        ],
      },
    ];

    const achievements = [
      { year: '2010', icon: '☀️', title: 'All Conference', detail: '1st Team' },
      { year: '2010', icon: 'AP', title: 'All American', detail: '1st Team' },
      { year: '2011', icon: 'NFL', title: 'NFL Draft', detail: '1st Round  Panthers' },
      { year: '2010', icon: '🏆', title: 'Davey O’Brien', detail: '' },
      { year: '2010', icon: '🏆', title: 'Heisman', detail: '' },
      { year: '2010', icon: '🏆', title: 'Maxwell Award', detail: '' },
      { year: '2010', icon: '🏆', title: 'Walter Camp', detail: '' },
      { year: '1-Year', icon: '▴', title: 'NCAA Starter', detail: '' },
      { year: '9-Year', icon: 'NFL', title: 'NFL Starter', detail: '' },
    ];

    const HSPrimaryPosition = primarySchool?.positions?.find((pos) => pos.isPrimary)?.code;
    const collegePrimaryPosition = primaryCollege?.positions?.find((pos) => pos.isPrimary)?.code;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <NamedBox name="Career" white>
          <ListItem
            icon={SchoolIcon}
            title='High School:'
          >
            {/* <div><b>2207 Grad - Franklin High School (QB)</b></div> */}
            <div><b>{primarySchool?.graduatingClass} Grad - {primarySchool?.name} ({HSPrimaryPosition})</b></div>
          </ListItem>
          {
            player?.playerColleges?.map((college, idx) => {
              const primaryPosition = college?.positions?.find((pos) => pos.isPrimary)?.code;
              return (
                <ListItem
                  key={idx}
                  icon={ClassIcon}
                  title={`College (${college?.recruitingClass}):`}
                >
                  <div><b>{college.team?.name} {college?.positions?.length ? `(${primaryPosition})` : ''}</b></div>
                </ListItem>
              );
            })
          }
          {/* <ListItem
            icon={ClassIcon}
            title='College (2007):'
          >
            <div><b>{primaryCollege?.team?.name} ({collegePrimaryPosition})</b></div>
          </ListItem>
          <ListItem
            icon={ClassIcon}
            title='College (2007):'
          >
            <div><b>Florida FR (QB)</b></div>
          </ListItem> */}
          <ListItem
            icon={TransferPortalIcon}
            title='Transfer Portal:'
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <div><b>Entered {player?.transferYear}</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Status {player?.transferStatus}</b></div>
              </div>
            </div>
            {/* <div><b>Grad Yes</b></div> */}
          </ListItem>
          <ListItem
            // icon={TransferPortalIcon}
            title=''
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <div><b>NCAA ID: 123123</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>DIV II</b></div>
              </div>
              <div className={classes.dividedContentSection}>
                <div><b>Year 25-26</b></div>
              </div>
            </div>

          </ListItem>
        </NamedBox>

        <NamedBox name="Achievements" white>
          <ListItem
          icon={SchoolIcon}
          title='2010'

          >
            <div>
              <Grid container>
                <Grid item xs={12} md={6}>
                  <b>1st Team</b>
                  <div className={classes.divider} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <b>2st Team</b>
                </Grid>
              </Grid>
            </div>
          </ListItem>
        </NamedBox>
      </div>
    );
  };

  const headerContent = (
    <div className={classes.headerInner}>
      <div className={classes.labelAndTabs}>
        {(['passing', 'rushing', 'receiving', 'defense'] as TabKey[]).map((tab) => (
          <button
            key={tab}
            type="button"
            className={clsx(classes.tab, activeTab === tab && classes.tabActive)}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      <div className={classes.actions}>
        {actionList.map((action, idx) => {
          const content = (
            <>
              {action.icon && <span className={classes.actionIcon}>{action.icon}</span>}
              {action.label}
            </>
          );
          const cls = clsx(classes.actionLink, action.disabled && classes.actionLinkDisabled);
          return (
            <span
              key={idx}
              role="button"
              className={cls}
              onClick={(event) => handleActionClick(event, action)}
            >
              {content}
            </span>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <NamedBox
        className={clsx(classes.root, className)}
        headerNameClassName={classes.headerName}
        name="Yearly Stats"
        headerContent={headerContent}
        white
      >
        <div className={classes.tableOuter}>
          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead className={classes.thead}>
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={String(col.key)}
                      className={clsx(
                        classes.th,
                        col.key === 'year' && classes.thFirst,
                        col.key === 'teamName' && classes.thSecond,
                      )}
                    >
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleSort(col.key as string)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, index) => {
                  const isTotals = (row as TotalsRow)._isTotals;
                  return (
                    <tr
                      key={isTotals ? 'totals' : `${row.year}-${row.teamName}-${index}`}
                      className={clsx(
                        classes.bodyRow,
                        !isTotals && index % 2 === 1 && classes.bodyRowAlt,
                        isTotals && classes.totalsRow,
                      )}
                    >
                      {columns.map((col) => renderCell(row, col))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </NamedBox>

      <Dialog
        open={isUsageDialogOpen}
        title="Usage"
        onClose={() => setIsUsageDialogOpen(false)}
        containsLongContent
      >
        {usageContent()}
      </Dialog>

      <Dialog
        title=''
        open={isCareerDialogOpen}
        onClose={() => setIsCareerDialogOpen(false)}
        // containsLongContent
        noHeader={true}
      >
        {careerContent()}
      </Dialog>

      <Dialog
        open={isGradesDialogOpen}
        title="PFF Grades"
        onClose={() => setIsGradesDialogOpen(false)}
        containsLongContent
        contentClassName={classes.fullGameLogDialogContent}
      >
        {isPlayerGameLogLoading ? (
          <Box className={classes.gameLogLoaderWrapper}>
            <CircularProgress size={28} />
          </Box>
        ) : fullGameLogRows.length > 0 ? (
          <PFFGameGradeLogTable useNamedBox={false} rows={fullGameLogRows} />
        ) : (
          <Box className={classes.gameLogEmptyState}>No game log data available.</Box>
        )}
      </Dialog>
    </>
  );
}
